import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, SignatureField } from '@symphenia/ui-components'
import { SITE_DOC_SIGNER_ROLES, type SiteDocDetail, type SiteDocProjectChoice, type SiteDocSignerRole } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { useTerminology } from '@/lib/useTerminology'
import { cn } from '@/lib/utils'
import { alsDataUrl, assignSiteDoc, countersignSiteDoc, fetchProjectChoices, fetchUserChoices, handSiteDocToDevice, voidSiteDoc } from '../api/site-docs.api'

const feld = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm'

function Knoepfe({ onClose, ok, okLabel, gefahr, disabled }: { onClose: () => void; ok: () => void; okLabel: string; gefahr?: boolean; disabled?: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full justify-end gap-2">
      <button type="button" onClick={onClose} className="h-10 rounded-md border px-4 text-sm">
        {t('common.cancel')}
      </button>
      <button type="button" disabled={disabled} onClick={ok} className={cn('h-10 rounded-md px-4 text-sm font-medium disabled:opacity-50', gefahr ? 'bg-red-600 text-white' : 'bg-primary text-primary-foreground')}>
        {okLabel}
      </button>
    </div>
  )
}

/** AP-46 5.3 — Stornieren, mit Grund; mit oder ohne Ersatz (BD-39). */
export function StornoDialog({ dok, onClose, onFertig }: { dok: SiteDocDetail; onClose: () => void; onFertig: (ersatzId: string | null) => void }) {
  const { t } = useTranslation()
  const [grund, setGrund] = useState('')
  const [ersatz, setErsatz] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)
  async function los() {
    setArbeitet(true)
    try {
      const r = await voidSiteDoc(dok.id, grund.trim(), ersatz)
      onFertig(r.replacement?.id ?? null)
    } catch (e) {
      setFehler(fehlertext(e, t))
      setArbeitet(false)
    }
  }
  return (
    <Modal isOpen onClose={onClose} title={t('siteDocs.detail.void')} size="md" footer={<Knoepfe onClose={onClose} ok={() => void los()} okLabel={t('siteDocs.detail.void')} gefahr disabled={grund.trim().length < 3 || arbeitet} />}>
      <div className="space-y-3 text-sm">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        <p>{t('siteDocs.detail.voidText', { nummer: dok.number })}</p>
        <textarea className="min-h-20 w-full rounded-md border border-input bg-background p-3" placeholder={t('siteDocs.release.reason')} value={grund} onChange={(e) => setGrund(e.target.value)} />
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={ersatz} onChange={(e) => setErsatz(e.target.checked)} /> {t('siteDocs.detail.voidWithReplacement')}
        </label>
      </div>
    </Modal>
  )
}

/** AP-46 8.1 — einen ERP-Entwurf an das Gerät eines Benutzers geben. */
export function AnGeraetDialog({ dok, onClose, onFertig }: { dok: SiteDocDetail; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation()
  const [leute, setLeute] = useState<{ id: string; name: string }[]>([])
  const [wer, setWer] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    fetchUserChoices().then(setLeute).catch((e) => setFehler(fehlertext(e, t)))
  }, [t])
  async function los() {
    if (!wer) return
    try {
      await handSiteDocToDevice(dok.id, wer)
      onFertig()
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }
  return (
    <Modal isOpen onClose={onClose} title={t('siteDocs.detail.handToDevice')} size="sm" footer={<Knoepfe onClose={onClose} ok={() => void los()} okLabel={t('siteDocs.detail.handToDevice')} disabled={!wer} />}>
      <div className="space-y-2 text-sm">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        <p className="text-muted-foreground">{t('siteDocs.detail.handToDeviceText')}</p>
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {leute.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => setWer(p.id)} className={cn('w-full rounded-md border px-3 py-1.5 text-left', wer === p.id && 'border-primary bg-primary/10')}>
                {p.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  )
}

/** AP-46 24. — „Projekt zuordnen": ein frei eingetragenes Projekt nachträglich einem Projekt zuordnen (protokolliert). */
export function ZuordnenDialog({ dok, onClose, onFertig }: { dok: SiteDocDetail; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const [suche, setSuche] = useState('')
  const [liste, setListe] = useState<SiteDocProjectChoice[]>([])
  const [wahl, setWahl] = useState<SiteDocProjectChoice | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    const z = setTimeout(() => fetchProjectChoices(suche.trim() || undefined).then((r) => setListe(r.data)).catch(() => setListe([])), suche ? 300 : 0)
    return () => clearTimeout(z)
  }, [suche])
  async function los() {
    if (!wahl) return
    try {
      await assignSiteDoc(dok.id, { project: { source: wahl.source, projectId: wahl.source === 'PROJECT' ? wahl.id : null, projectRefId: wahl.source === 'TIME_REF' ? wahl.id : null } })
      onFertig()
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }
  return (
    <Modal isOpen onClose={onClose} title={t('siteDocs.detail.assignProject', { project: terms.project })} size="md" footer={<Knoepfe onClose={onClose} ok={() => void los()} okLabel={t('siteDocs.detail.assign')} disabled={!wahl} />}>
      <div className="space-y-2 text-sm">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        <p className="text-muted-foreground">{t('siteDocs.detail.assignText')}</p>
        <input className={feld} placeholder={t('siteDocs.create.search')} value={suche} onChange={(e) => setSuche(e.target.value)} />
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {liste.map((p) => (
            <li key={`${p.source}-${p.id}`}>
              <button type="button" onClick={() => setWahl(p)} className={cn('w-full rounded-md border px-3 py-1.5 text-left', wahl?.id === p.id && 'border-primary bg-primary/10')}>
                {[p.number, p.name].filter(Boolean).join(' ')}
                {p.customerName && <span className="ml-2 text-xs text-muted-foreground">{p.customerName}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  )
}

/** AP-46 5.2 — Gegenzeichnen: eine weitere Unterschrift, gebunden an den vorhandenen Hash (BD-7). */
export function GegenzeichnenDialog({ dok, onClose, onFertig }: { dok: SiteDocDetail; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation()
  const [rolle, setRolle] = useState<SiteDocSignerRole>('SUPERVISOR')
  const [name, setName] = useState('')
  const [funktion, setFunktion] = useState('')
  const [bild, setBild] = useState<Blob | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)
  async function los() {
    if (!bild) return
    setArbeitet(true)
    try {
      await countersignSiteDoc(dok.kind, dok.id, { role: rolle, name: name.trim(), function: funktion.trim() || null, email: null, contactId: null, image: await alsDataUrl(bild) })
      onFertig()
    } catch (e) {
      setFehler(fehlertext(e, t))
      setArbeitet(false)
    }
  }
  return (
    <Modal isOpen onClose={onClose} title={t('siteDocs.detail.countersign')} size="lg" footer={<Knoepfe onClose={onClose} ok={() => void los()} okLabel={t('siteDocs.detail.countersign')} disabled={!bild || !name.trim() || arbeitet} />}>
      <div className="space-y-3 text-sm">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        <p className="text-muted-foreground">{t('siteDocs.detail.countersignText', { pruefwert: dok.contentHash?.slice(0, 8) })}</p>
        <div className="flex flex-wrap gap-2">
          {SITE_DOC_SIGNER_ROLES.map((r) => (
            <button key={r} type="button" onClick={() => setRolle(r)} className={cn('h-9 rounded-md border px-3', rolle === r && 'border-primary bg-primary/10')}>
              {t(`siteDocs.roles.${r}`)}
            </button>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <input className={feld} placeholder={t('siteDocs.release.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <input className={feld} placeholder={t('siteDocs.release.function')} value={funktion} onChange={(e) => setFunktion(e.target.value)} />
        </div>
        <SignatureField onChange={setBild} label={t('siteDocs.release.signature')} loeschenLabel={t('siteDocs.release.clear')} zuKurzLabel={t('siteDocs.release.tooShort')} />
      </div>
    </Modal>
  )
}
